import React, { ReactElement, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import { LinearProgress, Typography, Paper, Grid, Avatar, Box, Button } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SentimentSatisfiedOutlinedIcon from '@mui/icons-material/SentimentSatisfiedOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';

import DeedLogo from 'assets/icons/deed-logo.svg';
import config from 'config';
import { red } from 'utils/color-styles';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import { currencyFormat } from 'utils/currencyFormat';
import { ClickableUrl } from 'utils/urls';
import Img from 'retired/Img';
import PageHeaderRedesign from 'retired/PageHeaderRedesign';
import { selectUser } from 'entities/user/selectors';
import { selectOrganizationByIdOrExternalId } from 'entities/organization/selectors';
import { selectPartners } from 'entities/partners/selectors';
import { isNonprofitDBId } from 'utils/isNonprofitDBId';

import reducer from './reducer';
import epics from './epics';
import OrganizationAffiliationsTable from './OrganizationAffiliationsTable';
import {
  selectLoading,
  selectError,
  selectInitialized,
  selectTotalDonations,
  selectTotalVolunteerHours,
  selectTotalDeeds,
  selectTotalDonationsLoading,
  selectTotalVolunteerHoursLoading,
  selectTotalDeedsLoading,
} from './selectors';
import {
  init,
  fetchDonationsTotal,
  fetchDeedsTotal,
  fetchVolunteerTimesTotal,
  fetchPartnersAction,
  createPartnerAction,
  deletePartnerAction,
} from './types';
import { GridCustomItem } from './GridCustomItem/GridCustomItem';

const AvatarOrganizationLogoStyled = styled(Avatar)(() => ({
  border: `1px solid lightGray`,
  padding: '16px',
  width: '100%',
  height: '100%',
}));

const OrganizationDetailsPage = (): ReactElement => {
  const dispatch = useDispatch();
  useInjectEpics({
    key: 'organizationDetailsPage',
    epics,
  });
  useInjectReducer({
    key: 'organizationDetailsPage',
    reducer,
  });
  const history = useHistory();
  const {
    id: passedId,
  }: {
    id: string;
  } = useParams();

  const loading = useSelector(selectLoading);
  const organization = useSelector((state) => selectOrganizationByIdOrExternalId(state, passedId));
  const user = useSelector(selectUser);
  const error = useSelector(selectError);
  const initialized = useSelector(selectInitialized);

  const donationTotal = useSelector(selectTotalDonations);
  const volunteerHoursTotal = useSelector(selectTotalVolunteerHours);
  const deedsTotal = useSelector(selectTotalDeeds);

  const donationTotalLoadingState = useSelector(selectTotalDonationsLoading);
  const volunteerHoursTotalLoadingState = useSelector(selectTotalVolunteerHoursLoading);
  const deedsTotalLoadingState = useSelector(selectTotalDeedsLoading);

  const partnersList = useSelector(selectPartners);

  const id = organization?.id || passedId;
  const partner = partnersList.get(id);

  const isNonprofitDBNonprofit = isNonprofitDBId(id);

  useEffect(() => {
    if (user) {
      if (!user.hasPrivilege(Privileges.ORGANIZATIONS_VIEW) || !user.organization.isCompany()) {
        history.replace('/');
      }

      dispatch(init({ id }));

      if (!isNonprofitDBNonprofit) {
        dispatch(fetchDeedsTotal(id));

        if (user.hasPrivilege(Privileges.DONATION_VIEW) && user.hasFeature(Features.DONATIONS)) {
          dispatch(fetchDonationsTotal(id));
        }

        if (user.hasPrivilege(Privileges.VOLUNTEERTIME) && user.hasFeature(Features.VOLUNTEERING)) {
          dispatch(fetchVolunteerTimesTotal(id));
        }
        if (user.isAdmin || !config.isProduction) {
          dispatch(fetchPartnersAction());
        }
      }
    }
  }, [id, user]);

  const phoneNumber = organization?.phone
    ? parsePhoneNumberFromString(organization.phone, organization.locationObject.countryCode)
    : null;

  return (
    <>
      <Helmet title={organization?.name || 'Organization Details'} />
      {!initialized && loading && <LinearProgress />}
      {!loading && initialized && (
        <div>
          <PageHeaderRedesign
            title={organization?.name}
            onArrowClick={(): void =>
              history.length > 0 ? history.goBack() : history.push('/organizations')
            }
          >
            {(!config.isProduction || user?.isAdmin) &&
              user?.hasPrivilege(Privileges.PARTNER_MANAGE) && (
                <>
                  {!partner ? (
                    <Button
                      onClick={(): any => dispatch(createPartnerAction(id))}
                      endIcon={<AddCircleOutline />}
                    >
                      Add partner nonprofit
                    </Button>
                  ) : (
                    <Button
                      style={{ color: red, border: '1px solid #EC6545' }}
                      onClick={(): any => dispatch(deletePartnerAction(id))}
                      endIcon={<DeleteIcon />}
                    >
                      Remove partner nonprofit
                    </Button>
                  )}
                </>
              )}
          </PageHeaderRedesign>
        </div>
      )}

      {!loading && error && <Typography>There was an error loading the organization</Typography>}
      {initialized && !error && (
        <>
          <Paper
            variant="outlined"
            style={{
              padding: 32,
              marginBottom: 16,
            }}
          >
            <Grid container spacing={8}>
              <Grid item xs={3}>
                {organization.mainPicture && (
                  <AvatarOrganizationLogoStyled
                    src={organization.mainPicture}
                    variant="rounded"
                    imgProps={{ style: { objectFit: 'contain' } }}
                  />
                )}
              </Grid>

              <Grid item xs={9}>
                {user?.organization?.isCompany() && (
                  <Grid container spacing={2}>
                    {loading ||
                    donationTotalLoadingState ||
                    volunteerHoursTotalLoadingState ||
                    deedsTotalLoadingState ? (
                      <Box sx={{ width: '100%' }}>
                        <LinearProgress />
                      </Box>
                    ) : (
                      <>
                        {!isNonprofitDBNonprofit &&
                          user?.hasPrivilege(Privileges.DONATION_VIEW) &&
                          user?.hasFeature(Features.DONATIONS) && (
                            <GridCustomItem
                              title="Total Donations"
                              value={
                                donationTotal === null
                                  ? '—'
                                  : currencyFormat(donationTotal, user?.displayCurrency)
                              }
                              icon={<FavoriteIcon />}
                            />
                          )}

                        {!isNonprofitDBNonprofit &&
                          user?.hasPrivilege(Privileges.VOLUNTEERTIME) &&
                          user?.hasFeature(Features.VOLUNTEERING) && (
                            <GridCustomItem
                              title="Total Hours Volunteered"
                              value={
                                volunteerHoursTotal === null
                                  ? '—'
                                  : (
                                      Math.round((volunteerHoursTotal + Number.EPSILON) * 100) / 100
                                    ).toString()
                              }
                              icon={<SentimentSatisfiedOutlinedIcon />}
                            />
                          )}

                        {!isNonprofitDBNonprofit && (
                          <GridCustomItem
                            title="Total Deeds"
                            value={deedsTotal === null ? '—' : deedsTotal}
                            icon={
                              <Img
                                src={DeedLogo}
                                alt="External Link Icon"
                                style={{
                                  width: 16,
                                  height: 16,
                                }}
                              />
                            }
                          />
                        )}
                      </>
                    )}
                  </Grid>
                )}

                <Grid container spacing={2}>
                  <GridCustomItem
                    title="Location"
                    value={organization.locationObject
                      .toArray()
                      .filter((str: string) => str)
                      .join(', ')}
                    icon={<LocationOnIcon />}
                  />

                  <GridCustomItem
                    title="Phone"
                    value={
                      phoneNumber ? (
                        <a href={phoneNumber.getURI()}>{phoneNumber.formatInternational()}</a>
                      ) : (
                        '-'
                      )
                    }
                    icon={<PhoneEnabledIcon />}
                  />

                  <GridCustomItem
                    title="Employer Identification Number (EIN)"
                    value={organization.ein || '-'}
                    icon={<MoreHorizIcon />}
                  />
                </Grid>

                <Grid container spacing={2}>
                  <GridCustomItem title="Point of contact name" value="-" icon={<PersonIcon />} />

                  <GridCustomItem
                    title="Point of contact email"
                    value={
                      organization.email ? (
                        <a href={`mailto:${organization.email}`}>{organization.email}</a>
                      ) : (
                        '-'
                      )
                    }
                    icon={<EmailIcon />}
                  />

                  <GridCustomItem
                    title="Website"
                    value={
                      organization.website ? (
                        <ClickableUrl href={organization.website} target="_blank" rel="noreferrer">
                          {organization.website}
                        </ClickableUrl>
                      ) : (
                        '-'
                      )
                    }
                    icon={<DashboardOutlinedIcon />}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          {!isNonprofitDBNonprofit &&
            user?.hasFeature(Features.EMPLOYEE_AFFILIATIONS) &&
            user.hasPrivilege(Privileges.EMPLOYEE_AFFILIATIONS_VIEW) && (
              <OrganizationAffiliationsTable nonprofitId={id} />
            )}
        </>
      )}
    </>
  );
};

export default OrganizationDetailsPage;
